import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithAuth } from '@/services/api-base';
import type { ConfigurationSettingsState, PMSVendorData, PmsSourceKey } from '@/types/pms.types';
import type { PmsAccountRef } from './pmsAccount';
import { pmsEntityPath } from './pmsEntityPath';
import { withPropertyRefreshOn } from './propertyRefresh';

export type PmsConnectionWireStatus = 'ACTIVE' | 'INACTIVE';

/**
 * Credential-free view of the account's single PMS connection. The wire
 * record carried the DECRYPTED secret (DV-2550 handoff) until DV-3414 masked
 * it; either way it is dropped here so it never sits in the widely-subscribed
 * Redux cache. The vendor settings
 * page's form prefill reads the masked record via the axios getVendorsListAPI
 * (DV-3414).
 *
 * `unavailable` = the backend returned `status: null`, which means the
 * feature flag (feature.pms.connection.status.enabled) is OFF in that
 * environment — not that the integration is active. Surfaces must render
 * exactly as before DV-2549 in that case.
 */
export type IntegrationStatus =
  | { kind: 'none' }
  | { kind: 'unavailable'; pmsSource: PmsSourceKey; connection: ConnectionSummary }
  | { kind: 'active'; pmsSource: PmsSourceKey; enabledBy: string | null; enabledAt: string | null; connection: ConnectionSummary }
  | {
      kind: 'disabled';
      pmsSource: PmsSourceKey;
      enabledBy: string | null;
      enabledAt: string | null;
      disabledBy: string | null;
      disabledAt: string | null;
      connection: ConnectionSummary;
    };

/** Credential-free facts about the connection the landing hero renders. */
export interface ConnectionSummary {
  /** Wire `url` — the Rentvine/AppFolio subdomain; Buildium has none. */
  accountUrl: string | null;
  /** Wire `createdDate`. Always present, even with the status flag off. */
  connectedAt: string | null;
  settings: ConfigurationSettingsState;
}

/**
 * property-service writes `createdDate` as `String.valueOf(Instant.now())`, so
 * a real value is always an ISO-8601 timestamp. Anything else is dropped here
 * rather than handed to the hero: dayjs would format a bare epoch string to a
 * plausible-looking wrong year, not to "Invalid Date".
 */
const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T/;
const isoTimestampOrNull = (value: string | undefined): string | null =>
  value !== undefined && ISO_TIMESTAMP.test(value) && !Number.isNaN(Date.parse(value)) ? value : null;

const toConnectionSummary = (record: PMSVendorData): ConnectionSummary => ({
  accountUrl: record.url || null,
  connectedAt: isoTimestampOrNull(record.createdDate),
  settings: {
    autoSyncAssets: record.autoSyncAssets ?? false,
    watermarkedPhotos: record.watermarkedPhotos ?? false,
  },
});

export interface PmsConnectionStatusResponse {
  entity: string;
  pmsSource: string;
  status: PmsConnectionWireStatus;
  disabledBy: string | null;
  disabledAt: string | null;
  enabledBy: string | null;
  enabledAt: string | null;
  message: string;
}

const orNull = (value: string | null | undefined): string | null => value ?? null;

/** The account's one connection record from GET …/connect: an empty 200 body or an empty list is none. */
const connectionRecord = (wire: PMSVendorData[] | null | undefined): PMSVendorData | null => wire?.[0] ?? null;

export const toIntegrationStatus = (
  wire: PMSVendorData[] | null | undefined,
): IntegrationStatus => {
  const record = connectionRecord(wire);
  if (!record) return { kind: 'none' };
  const pmsSource = (record.pmsSource ?? '').toLowerCase() as PmsSourceKey;
  const connection = toConnectionSummary(record);
  if (record.status === 'INACTIVE') {
    return {
      kind: 'disabled',
      pmsSource,
      enabledBy: orNull(record.enabledBy),
      enabledAt: orNull(record.enabledAt),
      disabledBy: orNull(record.disabledBy),
      disabledAt: orNull(record.disabledAt),
      connection,
    };
  }
  if (record.status === 'ACTIVE') {
    return {
      kind: 'active',
      pmsSource,
      enabledBy: orNull(record.enabledBy),
      enabledAt: orNull(record.enabledAt),
      connection,
    };
  }
  return { kind: 'unavailable', pmsSource, connection };
};

/** Wire shape of GET …/syncLogs (property-service PmsSyncLogsResponse). Dates are epoch SECONDS. */
export interface PmsSyncLogWire {
  id: string | null;
  startDate: number | null;
  endDate: number | null;
  isSyncComplete: boolean | null;
  entity: string | null;
  errorMessage: string | null;
  pmsSource: string | null;
}

/**
 * Consumer-facing sync state. Derived here so no component repeats the
 * isSyncComplete / errorMessage decision table that PMSSync used to inline.
 */
export type SyncLogView =
  | { kind: 'never' }
  | { kind: 'running'; id: string; startedAt: string | null }
  | { kind: 'complete'; id: string; startedAt: string | null; endedAt: string | null }
  | { kind: 'failed'; id: string; startedAt: string | null; message: string | null };

const epochSecondsToIso = (seconds: number | null | undefined): string | null =>
  seconds == null ? null : new Date(seconds * 1000).toISOString();

export const toSyncLogView = (wire: PmsSyncLogWire | null | undefined): SyncLogView => {
  // The backend returns an empty 200 body when the entity has never synced;
  // fetchBaseQuery hands that to us as null.
  if (!wire || wire.id == null) return { kind: 'never' };
  const id = wire.id;
  const startedAt = epochSecondsToIso(wire.startDate);
  if (wire.isSyncComplete === true) {
    return { kind: 'complete', id, startedAt, endedAt: epochSecondsToIso(wire.endDate) };
  }
  if (wire.isSyncComplete == null && wire.errorMessage) {
    return { kind: 'failed', id, startedAt, message: wire.errorMessage };
  }
  return { kind: 'running', id, startedAt };
};

/** Result of the server-side stored-credential check (POST …/test, spec §8). */
export type ConnectionHealth = { kind: 'unknown' } | { kind: 'healthy' } | { kind: 'unhealthy' };

export const toConnectionHealth = (wire: { status?: string } | null | undefined): ConnectionHealth => {
  if (wire?.status === 'Success') return { kind: 'healthy' };
  if (wire?.status === 'Failed') return { kind: 'unhealthy' };
  return { kind: 'unknown' };
};

/**
 * The connection's stored credentials as property-service returns them —
 * already masked since DV-3414 (`key` → asterisks + last 4, `secret` → a fixed
 * mask; blank for AppFolio OAuth and Buildium). Read only by the internal
 * account page's read-only settings view (DV-3500); the status view model
 * above still drops them. Null when the account has no connection. Carries no
 * vendor: the view keys the rows off the status read's `pmsSource`.
 */
export interface ConnectionCredentials {
  url: string;
  key: string;
  secret: string;
}

export const toConnectionCredentials = (wire: PMSVendorData[] | null | undefined): ConnectionCredentials | null => {
  const record = connectionRecord(wire);
  if (!record) return null;
  return {
    url: record.url ?? '',
    key: record.key ?? '',
    secret: record.secret ?? '',
  };
};

export const pmsConnectionApi = createApi({
  reducerPath: 'pmsConnectionApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['PmsConnection', 'PmsSyncLog', 'PmsConnectionHealth'],
  endpoints: (builder) => ({
    // DV-3345: every endpoint takes the account; nothing here reads the session.
    getIntegrationStatus: builder.query<IntegrationStatus, PmsAccountRef>({
      query: (account) => `${pmsEntityPath(account)}/connect`,
      transformResponse: (wire: PMSVendorData[] | null) => toIntegrationStatus(wire),
      providesTags: ['PmsConnection'],
    }),
    // No pmsSource param on purpose: omitted, the backend uses the account's
    // single connection; sent with a typo it returns a misleading 400.
    setIntegrationStatus: builder.mutation<
      PmsConnectionStatusResponse,
      { account: PmsAccountRef; status: PmsConnectionWireStatus }
    >({
      query: ({ account, status }) => ({ url: `${pmsEntityPath(account)}/status?status=${status}`, method: 'PUT' }),
      invalidatesTags: ['PmsConnection'],
    }),
    getSyncLog: builder.query<SyncLogView, PmsAccountRef>({
      query: (account) => `${pmsEntityPath(account)}/syncLogs`,
      transformResponse: (wire: PmsSyncLogWire | null) => toSyncLogView(wire),
      providesTags: ['PmsSyncLog'],
    }),
    /**
     * Property refresh. Deliberately body-less: the backend runs the sync
     * against the account's stored connection, so this path can never
     * re-send credentials. Used by the Properties list, the landing hero and
     * re-enable.
     */
    syncProperties: builder.mutation<string, { account: PmsAccountRef; pmsSource: PmsSourceKey }>({
      query: ({ account, pmsSource }) => ({
        url: `${pmsEntityPath(account)}/sync?pmsSource=${pmsSource}`,
        method: 'POST',
        // The handler returns an untyped Object that is not always JSON.
        responseHandler: 'text',
      }),
      invalidatesTags: ['PmsSyncLog'],
    }),
    /**
     * Settings-page save. property-service saves the body via
     * saveOrUpdateConnection before syncing — this is what persists edited
     * credentials/settings today. Kept as a separate endpoint so the refresh
     * path above cannot accidentally carry a body.
     */
    saveConnectionAndSync: builder.mutation<string, { account: PmsAccountRef; body: PMSVendorData }>({
      query: ({ account, body }) => ({
        url: `${pmsEntityPath(account)}/sync`,
        method: 'POST',
        body: withPropertyRefreshOn(body),
        responseHandler: 'text',
      }),
      invalidatesTags: ['PmsSyncLog', 'PmsConnection', 'PmsConnectionHealth'],
    }),
    /**
     * Validates the STORED credentials server-side; no body, no secrets in
     * the response. A query (not a mutation) because it changes no state and
     * we want the cache + tag invalidation. Until the backend endpoint is
     * deployed and registered in Keycloak it answers 404/403 — consumers must
     * read errors as `unknown` (see useConnectionHealth), never as unhealthy.
     * Fail-open lives in the hook, not here: a `transformErrorResponse`
     * cannot do it (it populates `error`, never `data`), and the endpoint
     * keeps the genuine status so consumers can tell 403/404 from a 500.
     */
    getConnectionHealth: builder.query<ConnectionHealth, PmsAccountRef>({
      query: (account) => ({ url: `${pmsEntityPath(account)}/test`, method: 'POST' }),
      // A 403 here is the backend's answer (an Internal Admin until DV-3636),
      // and the hook fails open on it: never worth a token refresh (DV-3500).
      extraOptions: { forbiddenIsFinal: true },
      transformResponse: (wire: { status?: string } | null) => toConnectionHealth(wire),
      // Every fetch is a real authenticated request property-service makes to
      // the tenant's PMS vendor, and the backend has no throttling. RTK's 60 s
      // default would re-fire it on every Integrations remount after a minute
      // away; hold the last result for 5 minutes instead. A settings save
      // still refreshes it immediately via the PmsConnectionHealth tag.
      keepUnusedDataFor: 5 * 60,
      providesTags: ['PmsConnectionHealth'],
    }),
    /**
     * DV-3500: the masked credentials, for the internal account page's
     * read-only settings view. The same GET as getIntegrationStatus, kept as
     * its own endpoint so the status cache, which every surface subscribes
     * to, never holds credential fields. Dropped as soon as the view unmounts.
     */
    getConnectionCredentials: builder.query<ConnectionCredentials | null, PmsAccountRef>({
      query: (account) => `${pmsEntityPath(account)}/connect`,
      transformResponse: (wire: PMSVendorData[] | null) => toConnectionCredentials(wire),
      keepUnusedDataFor: 0,
      providesTags: ['PmsConnection'],
    }),
  }),
});

export const {
  useGetIntegrationStatusQuery,
  useSetIntegrationStatusMutation,
  useGetSyncLogQuery,
  useSyncPropertiesMutation,
  useSaveConnectionAndSyncMutation,
  useGetConnectionHealthQuery,
  useGetConnectionCredentialsQuery,
} = pmsConnectionApi;
